/**
 * Copyright 2022 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { type AxiosInstance } from 'axios';
import {
  mapCommerceIdToFulfillmentOptions,
  checkContainsSddEligibleItem,
  mapLineItemFulfillmentOptionGroupsFromAPI,
} from '../adaptors';
import { requestBaseURL } from '@/config/serviceAPI';
import { getAxios } from '@/lib/axios';
import type { AvailabilityInfoResponse, AvailabilityInfoData } from '../interface';
import { createQuery } from '@/utils/createReactQuery';
import { useSelector } from 'react-redux';
import type { ReduxState } from '@/types';
import actionConstant from '@/types/action';
import { useLegacyRouter } from '@/hooks/useLegacyRouter';

const URL = `${requestBaseURL}/ecomm/b2c/order/v4/availability`;

export const getAvailabilityInfoKey = () => ['availabilityInfo'] as const;

const getAvailabilityInfoData = async (axiosInstance?: AxiosInstance) => {
  const response = await getAxios(axiosInstance).get<AvailabilityInfoResponse>(URL);

  return selector(response.data);
};

const { useData: useAvailabilityInfo } = createQuery(
  'availabilityInfo',
  async (_options: object, axiosInstance?: AxiosInstance) => getAvailabilityInfoData(axiosInstance),
);

const selector = (data: AvailabilityInfoResponse): AvailabilityInfoData => {
  const { lineItemFulfillmentOptions, oneFulfillment } = data;
  const availabilityData = mapCommerceIdToFulfillmentOptions(lineItemFulfillmentOptions);
  const containsSddEligible = checkContainsSddEligibleItem(lineItemFulfillmentOptions);
  const itemAvailability = mapLineItemFulfillmentOptionGroupsFromAPI(lineItemFulfillmentOptions);

  const parsedAvailabilityInfo = {
    availabilityData,
    containsSddEligible,
    itemAvailability,
    oneFulfillment,
  };
  return parsedAvailabilityInfo;
};

// Helper function to check if availability calls should be enabled
export const useAvailabilityGatekeeper = () => {
  const readyStatus = useSelector((state: ReduxState) => state.orders.details.readyStatus);

  const router = useLegacyRouter();
  const isCartOrCheckoutPage =
    router.asPath?.includes('/cart') || router.asPath?.includes('/checkout');

  if (!isCartOrCheckoutPage) {
    return true;
  }

  return readyStatus === actionConstant.ORDER_DETAILS_SUCCESS;
};

export const useAvailabilityInfoData = () => {
  const isDetailsComplete = useAvailabilityGatekeeper();
  return useAvailabilityInfo({ enabled: isDetailsComplete });
};
